/**
 * Taxonomies Service Layer
 *
 * Provides a clean, type-safe interface for taxonomy operations.
 * Used by React Query hooks for data fetching and mutations.
 */

import { apiClient } from './api.client';
import type {
  Taxonomy,
  TaxonomyWithTerms,
  Term,
  TermWithTaxonomy,
  CreateTaxonomyDto,
  UpdateTaxonomyDto,
  CreateTermDto,
  UpdateTermDto,
  TaxonomyFilters,
  TermFilters,
} from '@lnr-v2/types';

// Re-export types for convenience
export type {
  Taxonomy,
  TaxonomyWithTerms,
  Term,
  TermWithTaxonomy,
  CreateTaxonomyDto,
  UpdateTaxonomyDto,
  CreateTermDto,
  UpdateTermDto,
  TaxonomyFilters,
  TermFilters,
};

interface TaxonomiesResponse {
  success: boolean;
  data: {
    items: Taxonomy[] | TaxonomyWithTerms[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

interface TaxonomyDetailResponse {
  success: boolean;
  data: TaxonomyWithTerms;
}

interface TermsResponse {
  success: boolean;
  data: {
    items: Term[] | TermWithTaxonomy[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

interface TermDetailResponse {
  success: boolean;
  data: TermWithTaxonomy;
}

/**
 * Taxonomies Service
 * All taxonomy-related API operations
 */
export const taxonomiesService = {
  /**
   * Get all taxonomies with optional filters
   */
  async getAll(filters?: TaxonomyFilters): Promise<TaxonomyWithTerms[]> {
    const searchParams = new URLSearchParams();

    // Only include term counts by default (not full terms for performance)
    searchParams.append('includeTermCounts', 'true');

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (
          value !== undefined &&
          key !== 'includeTerms' &&
          key !== 'includeTermCounts'
        ) {
          searchParams.append(key, String(value));
        }
      });
    }

    const response = await apiClient
      .get('taxonomies', { searchParams })
      .json<TaxonomiesResponse>();
    return response.data.items as TaxonomyWithTerms[];
  },

  /**
   * Get a single taxonomy by ID or slug
   */
  async getById(idOrSlug: string): Promise<TaxonomyWithTerms> {
    const response = await apiClient
      .get(`taxonomies/${idOrSlug}`)
      .json<TaxonomyDetailResponse>();
    return response.data;
  },

  /**
   * Create a new taxonomy
   */
  async create(data: CreateTaxonomyDto): Promise<TaxonomyWithTerms> {
    const response = await apiClient
      .post('taxonomies', { json: data })
      .json<TaxonomyDetailResponse>();
    return response.data;
  },

  /**
   * Update an existing taxonomy
   */
  async update(
    id: string,
    data: UpdateTaxonomyDto
  ): Promise<TaxonomyWithTerms> {
    const response = await apiClient
      .put(`taxonomies/${id}`, { json: data })
      .json<TaxonomyDetailResponse>();
    return response.data;
  },

  /**
   * Delete a taxonomy (soft delete)
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`taxonomies/${id}`);
  },

  /**
   * Hard delete a taxonomy
   */
  async hardDelete(id: string): Promise<void> {
    await apiClient.delete(`taxonomies/${id}/hard`);
  },
};

/**
 * Terms Service
 * All term-related API operations
 */
export const termsService = {
  /**
   * Get all terms with optional filters
   */
  async getAll(filters?: TermFilters): Promise<TermWithTaxonomy[]> {
    const searchParams = new URLSearchParams();

    // Always include taxonomy by default
    searchParams.append('includeTaxonomy', 'true');
    searchParams.append('includeUsageCounts', 'true');

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (
          value !== undefined &&
          key !== 'includeTaxonomy' &&
          key !== 'includeUsageCounts'
        ) {
          searchParams.append(key, String(value));
        }
      });
    }

    const response = await apiClient
      .get('terms', { searchParams })
      .json<TermsResponse>();
    return response.data.items as TermWithTaxonomy[];
  },

  /**
   * Get terms grouped by taxonomy
   */
  async getGrouped(): Promise<Record<string, TermWithTaxonomy[]>> {
    const response = await apiClient
      .get('terms/grouped')
      .json<{ success: boolean; data: Record<string, TermWithTaxonomy[]> }>();
    return response.data;
  },

  /**
   * Get a single term by ID or slug
   */
  async getById(
    idOrSlug: string,
    taxonomyId?: string
  ): Promise<TermWithTaxonomy> {
    const searchParams = new URLSearchParams();
    if (taxonomyId) {
      searchParams.append('taxonomyId', taxonomyId);
    }
    const response = await apiClient
      .get(`terms/${idOrSlug}`, { searchParams })
      .json<TermDetailResponse>();
    return response.data;
  },

  /**
   * Create a new term
   */
  async create(data: CreateTermDto): Promise<TermWithTaxonomy> {
    const response = await apiClient
      .post('terms', { json: data })
      .json<TermDetailResponse>();
    return response.data;
  },

  /**
   * Update an existing term
   */
  async update(id: string, data: UpdateTermDto): Promise<TermWithTaxonomy> {
    const response = await apiClient
      .put(`terms/${id}`, { json: data })
      .json<TermDetailResponse>();
    return response.data;
  },

  /**
   * Delete a term (soft delete)
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`terms/${id}`);
  },

  /**
   * Hard delete a term
   */
  async hardDelete(id: string): Promise<void> {
    await apiClient.delete(`terms/${id}/hard`);
  },
};
